import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, XCircle, Shield } from "lucide-react";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState("loading");
  const [plan, setPlan] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId || !token) return;

      try {
        const response = await axios.get(
          `${API}/subscription/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.payment_status === "paid") {
          setStatus("success");
          setPlan(response.data.plan);
          await refreshUser();
          toast.success("Paiement réussi ! Bienvenue dans SHARE Premium !");
        } else if (response.data.status === "expired") {
          setStatus("error");
        } else if (attempts < 5) {
          // Keep polling
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch (error) {
        console.error("Error checking payment:", error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, token, attempts, refreshUser]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF0F5] to-white flex items-center justify-center p-4">
      <Card className="guardian-card max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Loader2 size={40} className="text-blue-500 animate-spin" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Vérification du paiement...
              </h1>
              <p className="text-slate-500">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-green-500" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Paiement réussi ! 🎉
              </h1>
              <p className="text-slate-500 mb-6">
                Votre abonnement est maintenant actif. Bienvenue dans SHARE Premium !
              </p>
              <Button onClick={() => navigate("/dashboard")} className="btn-guardian rounded-full">
                <Shield size={18} className="mr-2" />
                Accéder à mon compte
              </Button>
            </>
          )}

          {(status === "error" || status === "timeout") && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle size={40} className="text-red-500" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                {status === "timeout" ? "Délai dépassé" : "Erreur de paiement"}
              </h1>
              <p className="text-slate-500 mb-6">
                {status === "timeout" 
                  ? "Nous n'avons pas pu confirmer votre paiement. Veuillez vérifier votre email pour la confirmation."
                  : "Le paiement a échoué ou a expiré. Veuillez réessayer."
                }
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate("/pricing")} className="rounded-full">
                  Réessayer
                </Button>
                <Button onClick={() => navigate("/dashboard")} className="btn-guardian rounded-full">
                  Tableau de bord
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
